import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  TrashIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/api';

const HSCodeManagement = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [validateCode, setValidateCode] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [fbrCodes, setFbrCodes] = useState([]);
  const [showFbrCodes, setShowFbrCodes] = useState(false);
  const [fbrLoading, setFbrLoading] = useState(false);

  useEffect(() => {
    loadHSCodeStats();
  }, []);

  const loadHSCodeStats = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/hscode-sync/status');
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to load HS Code stats:', error);
      toast.error('Failed to load HS Code statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      toast.loading('Starting HS Code synchronization...', { id: 'sync' });
      
      const response = await apiClient.post('/hscode-sync/synchronize');
      
      if (response.data.success) {
        toast.success(
          `Sync completed! Added: ${response.data.statistics.added}, Updated: ${response.data.statistics.updated}`,
          { id: 'sync', duration: 5000 }
        );
        await loadHSCodeStats(); // Reload stats
      } else {
        toast.error('Synchronization failed', { id: 'sync' });
      }
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error(
        error.response?.data?.message || 'Synchronization failed',
        { id: 'sync' }
      );
    } finally {
      setSyncing(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await apiClient.get(`/hscode-sync/search?q=${encodeURIComponent(searchTerm)}&limit=20`);
      setSearchResults(response.data.data);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!validateCode.trim()) {
      setValidationResult(null);
      return;
    }

    try {
      setValidating(true);
      const response = await apiClient.get(`/hscode-sync/validate/${encodeURIComponent(validateCode)}`);
      setValidationResult(response.data.data);
    } catch (error) {
      console.error('Validation failed:', error);
      toast.error('Validation failed');
      setValidationResult(null);
    } finally {
      setValidating(false);
    }
  };

  const loadFbrCodes = async () => {
    try {
      setFbrLoading(true);
      const response = await apiClient.get('/hscode-sync/fbr-codes?limit=50');
      setFbrCodes(response.data.data);
      setShowFbrCodes(true);
    } catch (error) {
      console.error('Failed to load FBR codes:', error);
      toast.error('Failed to load FBR codes');
    } finally {
      setFbrLoading(false);
    }
  };

  const clearCache = async () => {
    try {
      await apiClient.delete('/hscode-sync/cache');
      toast.success('Cache cleared successfully');
      await loadHSCodeStats();
    } catch (error) {
      console.error('Failed to clear cache:', error);
      toast.error('Failed to clear cache');
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, description, action }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className={`h-6 w-6 ${color}`} />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd className="text-lg font-medium text-gray-900">{value}</dd>
              {description && (
                <dd className="text-xs text-gray-400 mt-1">{description}</dd>
              )}
            </dl>
          </div>
          {action && (
            <div className="ml-5 flex-shrink-0">
              {action}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">HS Code Management</h2>
            <p className="mt-1 text-sm text-gray-600">
              Manage and synchronize HS codes with FBR Digital Invoicing API
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={clearCache}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Clear Cache
            </button>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Synchronizing...' : 'Sync with FBR'}
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total HS Codes"
          value={stats?.totalHSCodes || 0}
          icon={DocumentTextIcon}
          color="text-blue-500"
          description="In database"
        />
        <StatCard
          title="Active HS Codes"
          value={stats?.activeHSCodes || 0}
          icon={CheckCircleIcon}
          color="text-green-500"
          description="Valid and active"
        />
        <StatCard
          title="Inactive HS Codes"
          value={stats?.inactiveHSCodes || 0}
          icon={XCircleIcon}
          color="text-red-500"
          description="Deactivated codes"
        />
        <StatCard
          title="Cache Status"
          value={stats?.cacheStatus?.isCached ? 'Cached' : 'No Cache'}
          icon={ClockIcon}
          color={stats?.cacheStatus?.isCached ? 'text-green-500' : 'text-gray-500'}
          description={stats?.cacheStatus?.isCached ? 
            `${stats.cacheStatus.cacheSize} codes cached` : 
            'No cached data'
          }
        />
      </div>

      {/* Last Updated Info */}
      {stats?.lastUpdated && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <InformationCircleIcon className="h-5 w-5 text-blue-500 mr-2" />
            <span className="text-sm text-blue-700">
              Last updated: {new Date(stats.lastUpdated).toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* Tools Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* HS Code Validation */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Validate HS Code</h3>
          <div className="space-y-4">
            <div className="flex space-x-2">
              <input
                type="text"
                value={validateCode}
                onChange={(e) => setValidateCode(e.target.value)}
                placeholder="Enter HS Code (e.g., 8432.1010)"
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleValidate()}
              />
              <button
                onClick={handleValidate}
                disabled={validating || !validateCode.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {validating ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircleIcon className="h-4 w-4" />
                )}
              </button>
            </div>

            {validationResult && (
              <div className={`p-4 rounded-lg border ${
                validationResult.isValid 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start">
                  {validationResult.isValid ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                  )}
                  <div>
                    <p className={`text-sm font-medium ${
                      validationResult.isValid ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {validationResult.isValid ? 'Valid HS Code' : 'Invalid HS Code'}
                    </p>
                    <p className={`text-sm mt-1 ${
                      validationResult.isValid ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {validationResult.isValid 
                        ? validationResult.description 
                        : validationResult.error || 'HS code not found in FBR database'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* HS Code Search */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Search HS Codes</h3>
          <div className="space-y-4">
            <div className="flex space-x-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by code or description..."
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button
                onClick={handleSearch}
                disabled={searchLoading || !searchTerm.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {searchLoading ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <MagnifyingGlassIcon className="h-4 w-4" />
                )}
              </button>
            </div>

            {searchResults.length > 0 && (
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                {searchResults.map((result, index) => (
                  <div key={index} className="p-3 border-b border-gray-100 last:border-b-0">
                    <div className="font-medium text-sm text-gray-900">{result.code}</div>
                    <div className="text-xs text-gray-600 mt-1">{result.description}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FBR Codes Preview */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">FBR API Preview</h3>
          <button
            onClick={loadFbrCodes}
            disabled={fbrLoading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {fbrLoading ? (
              <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <EyeIcon className="h-4 w-4 mr-2" />
            )}
            {fbrLoading ? 'Loading...' : 'Load FBR Codes'}
          </button>
        </div>

        {showFbrCodes && fbrCodes.length > 0 && (
          <div className="border border-gray-200 rounded-md">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
              <p className="text-sm text-gray-600">
                Showing first 50 HS codes from FBR API (Total: {fbrCodes.length} loaded)
              </p>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {fbrCodes.map((code, index) => (
                <div key={index} className="p-3 border-b border-gray-100 last:border-b-0">
                  <div className="font-medium text-sm text-gray-900">{code.hS_CODE}</div>
                  <div className="text-xs text-gray-600 mt-1">{code.description}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <InformationCircleIcon className="h-5 w-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-yellow-800">HS Code Management Help</h4>
            <div className="text-sm text-yellow-700 mt-1 space-y-1">
              <p>• <strong>Sync with FBR:</strong> Updates local database with latest HS codes from FBR API</p>
              <p>• <strong>Validate:</strong> Check if an HS code is valid according to FBR database</p>
              <p>• <strong>Search:</strong> Find HS codes by code number or description</p>
              <p>• <strong>Clear Cache:</strong> Removes cached FBR data to force fresh API calls</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HSCodeManagement;